import api from "../../api/api";

/**
 * Thin wrappers over the assistant endpoints.
 *
 * The shared axios instance already injects the bearer token and
 * Accept-Language, which the agent uses to pick its reply language.
 */

export const listConversations = () =>
  api.get("/assistant/conversations").then((r) => r.data.conversations || []);

export const createConversation = (title) =>
  api.post("/assistant/conversations", { title }).then((r) => r.data.conversation);

export const renameConversation = (uid, title) =>
  api.patch(`/assistant/conversations/${uid}`, { title }).then((r) => r.data.conversation);

export const deleteConversation = (uid) =>
  api.delete(`/assistant/conversations/${uid}`).then((r) => r.data);

export const getMessages = (uid) =>
  api.get(`/assistant/conversations/${uid}/messages`).then((r) => r.data);

export const getSuggestions = () =>
  api.get("/assistant/suggestions").then((r) => r.data.suggestions || []);

/**
 * How long to wait for an answer before giving up.
 *
 * A typical turn is 1-6s, but a multi-tool question can be slower. The point of
 * the cap is that a dropped connection (a backend restart, a dead network) must
 * surface as an error rather than leaving the typing indicator spinning forever
 * — the shared axios instance has no timeout of its own.
 */
export const SEND_TIMEOUT_MS = 120000;

/** Send a message. Omit `uid` to start a new conversation server-side. */
export const sendMessage = async ({ uid, text, image, countingJobId, transcriptionIds }) => {
  if (image?.type?.startsWith("video/")) throw Object.assign(new Error("video_disabled"), { code: "video_disabled" });
  const form = new FormData();
  if (text) form.append("text", text);
  if (image) form.append("image", image);
  if (image) form.append("counting_async", "1");
  if (countingJobId) form.append("counting_job_id", countingJobId);
  if (transcriptionIds?.length) form.append("transcription_ids", JSON.stringify(transcriptionIds.slice(0, 10)));
  const url = uid ? `/assistant/conversations/${uid}/messages` : "/assistant/messages";
  const response = await api.post(url, form, { timeout: SEND_TIMEOUT_MS });
  if (!response.data?.counting_pending) return response.data;
  const id = response.data.counting_job_id;
  // Queue + compute are bounded independently of the normal chat HTTP timeout.
  await waitForCounting(id);
  form.set("counting_job_id", id);
  return api.post(url, form, { timeout: SEND_TIMEOUT_MS }).then((r) => r.data);
};

export async function waitForCounting(id, signal) {
  const deadline = Date.now() + 390000;
  while (Date.now() < deadline && !signal?.aborted) {
    const job = await getCountingJob(id, signal);
    if (job.state === "done") return job;
    if (job.state === "failed") throw Object.assign(new Error("counting_failed"), { code: job.error });
    await new Promise((resolve) => {
      const done = () => { clearTimeout(timer); signal?.removeEventListener("abort", done); resolve(); };
      const timer = setTimeout(done, 2000);
      signal?.addEventListener("abort", done, { once: true });
    });
  }
  throw Object.assign(new Error("counting_timeout"), { code: "processing_timeout" });
}

export const getCountingCapabilities = () =>
  api.get("/assistant/counting/capabilities", { timeout: 5000 }).then((r) => r.data);

export const submitCounting = (media, modelId, target) => {
  const form = new FormData();
  form.append("model_id", modelId);
  if (target) form.append("target", target);
  form.append("media", media);
  return api.post("/assistant/counting/jobs", form, { timeout: 30000 }).then((r) => r.data);
};

export const getCountingJob = (id, signal) =>
  api.get(`/assistant/counting/jobs/${id}`, { timeout: 5000, signal }).then((r) => r.data);

export const getCountingPreview = (id, signal) =>
  api.get(`/assistant/counting/jobs/${id}/preview`, { timeout: 5000, responseType: "blob", signal }).then((r) => r.data);

/** Both surfaces refetch when the other one sends, mirroring the app's
 *  existing `user-updated` window-event pattern. */
export const ASSISTANT_UPDATED = "assistant:updated";
export const notifyAssistantUpdated = (conversationUid, source) =>
  window.dispatchEvent(
    new CustomEvent(ASSISTANT_UPDATED, { detail: { conversationUid, source } })
  );
